import { useCallback, useMemo } from 'react';
import { useAuthModal } from '@cx-web/feature-myoc-login-2018/provider';
import { type CxRuntimeAdapter } from '@cx-web/feature-cx-runtime';
import { useLabelActions } from '@cx-web/feature-i18n';
import TrackingActions from '@cx-web/feature-tracking';
import { useUIConfig } from '@cx-web/ui-shell/ui-config-context';

// NGF-6334: the RR7 (atlas) implementation of the CX runtime adapter. Each seam maps to the
// existing page-template provider this app already mounts, so shared page-template components read
// labels/config/auth/tracking through `useCxRuntime()` instead of importing these hooks
// directly. Mounted once via `CxRuntimeProvider` in StandardLayout (inside UIConfigProvider, with
// LabelProvider + AuthModalProvider as route-level ancestors). Responsive behaviour is intentionally
// NOT a seam — shared components do it in CSS — so this adapter no longer reads MediaProvider.

// Tracking is stateless (no provider): a module-stable wrapper maps the adapter's `track(event,
// payload)` onto TrackingActions.eventTracking's single-object `{ event, ... }` shape.
const track = (event: string, payload: Record<string, unknown>): void =>
  new TrackingActions().eventTracking({ event, ...payload });
const atlasTracking = { track };

export const atlasCxRuntime: CxRuntimeAdapter = {
  useLabel: () => {
    // Wrap (not return-detached): LabelService.get reads `this.storeLabels`/`this.DEV_ENV`, so the
    // bare method loses its instance when called as `t(key)`. Call it as a method to preserve `this`.
    const labelActions = useLabelActions();
    return useCallback((key: string) => labelActions.get(key), [labelActions]);
  },
  useConfig: () => {
    const { resourcesUrl } = useUIConfig();
    return useMemo(() => ({ resourcesUrl }), [resourcesUrl]);
  },
  // Pass the modal's general open-at-view through unchanged; callers (e.g. onboarding) pick the view.
  useAuthModal: () => {
    const { openAtView } = useAuthModal();
    return useMemo(() => ({ openAtView }), [openAtView]);
  },
  useTracking: () => atlasTracking,
};
